/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  Switch,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import { useParams, useHistory } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import {
  getSiteManagerDetails,
  updateSiteManager
} from 'src/redux/siteManagerSlice';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import shouldDisplayDetailField from 'src/utils/detailFieldUtils';
import ROUTE from 'src/utils/routeConstants';
import NewSiteManager from './NewSiteManager';

/* ---------- Smart Text Component ---------- */
function SmartText({ value }) {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      setShowTooltip(
        textRef.current.scrollWidth > textRef.current.clientWidth
      );
    }
  }, [value]);

  return (
    <Tooltip title={showTooltip ? value : ''} arrow>
      <Typography
        ref={textRef}
        variant="body1"
        fontWeight={600}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {value ?? '--'}
      </Typography>
    </Tooltip>
  );
}

/* ---------- Helpers ---------- */

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined) return '--';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return value;
};

const isSiteAssignmentField = (key, details) => (
  key === 'site'
  || key === 'sites'
  || (key === 'site_name' && Array.isArray(details.site_names))
);

/* ---------- Component ---------- */

function SiteSiteManagerDetails() {
  const theme = useTheme();
  const { userId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, siteManagerDetails } = useSelector(
    (state) => state.siteManagers
  );

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getSiteManagerDetails(userId));
  }, [dispatch, userId]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    try {
      const values = {
        ...siteManagerDetails,
        is_active: !siteManagerDetails.is_active
      };
      await dispatch(updateSiteManager(values)).unwrap();
      dispatch(
        setSuccessInfo({
          successMsg: 'Supervisor status updated successfully!'
        })
      );
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: error?.error_message }));
    }
    setIsLoading(false);
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NewSiteManager
        open={openModal}
        initialValues={siteManagerDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)',
        }}
      >
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                SITE SUPERVISOR DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage supervisor information
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.siteManagers)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main
                }
              }}
            >
              Back
            </Button>
          )}
        />

        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {Object.entries(siteManagerDetails || {}).map(
              ([key, value]) => {
                if (!shouldDisplayDetailField(key) || isSiteAssignmentField(key, siteManagerDetails)) {
                  return null;
                }

                if (key === 'is_active') {
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(
                              theme.palette.primary.main,
                              0.02
                            )
                          }
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            STATUS
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={
                              value ? 'success.main' : 'error.main'
                            }
                          >
                            {value ? 'Active' : 'Inactive'}
                          </Typography>
                        </Box>

                        <Switch
                          checked={value}
                          onChange={() => setShowConfirmModal(true)}
                          color="success"
                        />
                      </Box>
                    </Grid>
                  );
                }

                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(
                            theme.palette.primary.main,
                            0.02
                          )
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}
                      >
                        {formatLabel(key)}
                      </Typography>

                      <SmartText value={formatValue(value)} />
                    </Box>
                  </Grid>
                );
              }
            )}
          </Grid>
        </CardContent>

        <Divider />

        <CardActions sx={{
          p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
        }}
        >
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            onClick={() => setOpenModal(true)}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Edit Supervisor Details
          </Button>
        </CardActions>
      </Card>

      <ConfirmationDialog
        info={
          siteManagerDetails.is_active
            ? CONFIRMATION_MSG.disableSiteManager
            : CONFIRMATION_MSG.enableSiteManager
        }
        open={showConfirmModal}
        disable={siteManagerDetails.is_active}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

export default SiteSiteManagerDetails;
